import React, { useState, useEffect, useCallback } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { Form, Select, Group, Input, FormActions, Modal } from '@peracto/peracto-ui'
import { DELETE, GET_LIST, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { Field } from 'formik'

export const MODE_ADD = 'add'
export const MODE_EDIT = 'edit'

const ProductMenuItemRelationsForm = ({ mode = MODE_EDIT, values, ...props }) => {
    const config = useConfig()
    const { client } = useClient()
    const { id, relationId } = useParams()
    const { products } = config.get('features', {})
    const readOnly = products?.readOnly

    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [redirect, setRedirect] = useState()
    const [menuItems, setMenuItems] = useState([])

    const onDelete = async () => {
        try {
            await client(DELETE, 'product-menu-item-relations', {
                id: `/product-menu-item-relations/${relationId}`,
            })

            toast.success('Product menu item relation deleted successfully!')
            setRedirect(`/products/${id}/menu-item-relations`)
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        }
    }

    const getMenuItems = useCallback(async () => {
        try {
            const { data: menuItemData } = await client(GET_LIST, 'menu-items', {
                id: 'menu-items',
                pagination: {
                    page: null,
                    perPage: 2000,
                },
            })

            setMenuItems(menuItemData)
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        }
    }, [])

    const findMenuItemLabel = menuItemValue => {
        const menuItem = menuItems.find(
            menuItem => `/menu-items/${menuItem?.originId}` === menuItemValue
        )

        return menuItem?.linkText || 'N/A'
    }

    useEffect(() => {
        if (!products?.productMenuItemRelations) {
            setRedirect(`/products/${id}`)
        } else {
            getMenuItems()
        }
    }, [products, getMenuItems])

    return (
        <>
            {redirect && <Redirect to={redirect} />}

            {mode === MODE_EDIT && !readOnly && (
                <FormActions>
                    <a
                        className="!text-danger"
                        onClick={() => setShowDeleteDialog(true)}
                        data-testid="delete-product-menu-item-relation"
                    >
                        <FontAwesomeIcon icon={faTrash} className="!mr-2" />
                        Delete Product Menu Item Relation
                    </a>
                </FormActions>
            )}

            <Form
                autoComplete="off"
                {...props}
                values={{
                    product: `/products/${id}`,
                    menuItems: '',
                    displayOrder: 0,
                    label: '',
                    ...values,
                }}
            >
                <div data-testid="core-section">
                    <Group key="core" id="core" name="Core">
                        <div className="form-row">
                            <div className="col-12">
                                <Field name="menuItems">
                                    {({ form }) => (
                                        <Select
                                            name="menuItems"
                                            label="Menu Item"
                                            help="Menu item of the product menu item relation"
                                            disabled={readOnly}
                                            testId="menu-item"
                                            options={menuItems?.map(menuItem => ({
                                                label: menuItem?.linkText,
                                                value: `/menu-items/${menuItem?.originId}`,
                                            }))}
                                            handleChange={value =>
                                                form.setFieldValue(
                                                    'label',
                                                    findMenuItemLabel(value)
                                                )
                                            }
                                        />
                                    )}
                                </Field>
                            </div>
                            <div className="col-12">
                                <Input
                                    name="label"
                                    label="Label"
                                    help="Label of the product menu item relation"
                                    testId="label"
                                    disabled={readOnly}
                                />
                            </div>
                            <div className="col-12">
                                <Input
                                    name="displayOrder"
                                    label="Display Order"
                                    help="Display order of the product menu item relation"
                                    disabled={readOnly}
                                    type="number"
                                    testId="display-order"
                                />
                            </div>
                        </div>
                    </Group>
                </div>
            </Form>

            {mode === MODE_EDIT && (
                <>
                    <Modal
                        isVisible={showDeleteDialog}
                        title="Delete Product Menu Item Relation"
                        close={() => setShowDeleteDialog(false)}
                        buttons={[
                            {
                                type: 'btn-outline-secondary',
                                text: 'Close',
                                action: () => setShowDeleteDialog(false),
                            },
                            {
                                type: 'btn-danger',
                                text: 'Delete Product Menu Item Relation',
                                action: () => onDelete(),
                            },
                        ]}
                    >
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            size="4x"
                            className="!mb-6 !block"
                        />
                        Are you sure you would like to permanently delete this product menu item
                        relation?
                    </Modal>
                </>
            )}
        </>
    )
}

ProductMenuItemRelationsForm.displayName = 'ProductMenuItemRelationsForm'

ProductMenuItemRelationsForm.propTypes = {
    values: PropTypes.object,
    mode: PropTypes.oneOf([MODE_ADD, MODE_EDIT]),
    schema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default ProductMenuItemRelationsForm
